import {
  type Block,
  type Format,
  type Inline,
  type List,
  type MentionName,
  parse,
  safeHref,
} from "@rhema/format";
import { Fragment, type MouseEvent, memo, type ReactNode, useMemo } from "react";
import { MentionChip } from "../Badge";
import { cn, focusRing } from "../utils";
import { CodeBlock } from "./CodeBlock";

// Shows a message body as React elements. The body is parsed by @rhema/format into plain nodes
// and nothing is ever inserted as HTML, so a message cannot run code or change the page.

export type RichTextProps = {
  body: string;
  /** "md" reads the formatting marks. Without it the body shows as plain text, as always. */
  format?: Format;
  /** Members the message mentions, so their @Name can be shown as a mention. */
  mentions?: readonly MentionName[];
  /** Opens a web or email link outside Rhema, in the system browser or mail app. */
  openExternal: (href: string) => void;
  /** Shows a mention. Defaults to the gold mention chip. */
  renderMention?: (mention: MentionName) => ReactNode;
  /**
   * Shows a #channel reference. Return a link for a channel the reader can open and plain text
   * otherwise. Defaults to plain text.
   */
  renderChannel?: (name: string) => ReactNode;
  /** Writes code to the clipboard for a code block's Copy button. Defaults to the browser's. */
  copy?: (text: string) => Promise<void> | void;
  /**
   * Shows a run of plain text, such as Queravel's answers turning [1] into a link to a source.
   * Text inside links and code is left as it is.
   */
  renderText?: (text: string) => ReactNode;
  className?: string;
};

type Render = Pick<RichTextProps, "openExternal" | "renderMention" | "renderChannel" | "copy" | "renderText">;

export const linkClass = cn(
  "cursor-pointer break-words text-blue underline decoration-blue/40 underline-offset-2 hover:decoration-blue",
  focusRing,
);

function ExternalLink({
  href,
  children,
  open,
}: {
  href: string;
  children: ReactNode;
  open: (href: string) => void;
}) {
  // The parser only makes links for safe addresses. This checks again in case a tree came from
  // anywhere else.
  const safe = safeHref(href);
  if (!safe) return <>{children}</>;
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    open(safe);
  };
  return (
    <a href={safe} onClick={onClick} rel="noopener noreferrer nofollow" target="_blank" className={linkClass}>
      {children}
    </a>
  );
}

function inline(n: Inline, r: Render): ReactNode {
  switch (n.type) {
    case "text":
      return r.renderText ? r.renderText(n.text) : n.text;
    case "bold":
      return <strong className="font-semibold">{inlines(n.children, r)}</strong>;
    case "italic":
      return <em>{inlines(n.children, r)}</em>;
    case "strike":
      return <s>{inlines(n.children, r)}</s>;
    case "code":
      return (
        <code className="rounded-[5px] border border-hairline bg-[#f1efe9] px-[5px] py-px font-mono text-[0.87em] text-[#8f3550] [overflow-wrap:anywhere]">
          {n.text}
        </code>
      );
    case "link":
      return (
        <ExternalLink href={n.href} open={r.openExternal}>
          {inlines(n.children, { ...r, renderText: undefined })}
        </ExternalLink>
      );
    case "mention":
      return r.renderMention ? (
        r.renderMention({ id: n.id, name: n.name })
      ) : (
        <MentionChip>@{n.name}</MentionChip>
      );
    case "channel":
      return r.renderChannel ? r.renderChannel(n.name) : `#${n.name}`;
  }
}

function inlines(nodes: readonly Inline[], r: Render): ReactNode[] {
  // biome-ignore lint/suspicious/noArrayIndexKey: nodes keep their order for a given body
  return nodes.map((n, i) => <Fragment key={i}>{inline(n, r)}</Fragment>);
}

function ListView({ list, r, nested }: { list: List; r: Render; nested?: boolean }) {
  const Tag = list.ordered ? "ol" : "ul";
  return (
    <Tag
      start={list.ordered && list.start !== 1 ? list.start : undefined}
      className={cn("m-0 pl-[22px]", list.ordered ? "list-decimal" : nested ? "list-[circle]" : "list-disc")}
    >
      {list.items.map((item, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: items keep their order for a given body
        <li key={i} className="pl-0.5 whitespace-pre-wrap">
          {inlines(item.children, r)}
          {item.lists.map((sub, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: lists keep their order for a given body
            <ListView key={j} list={sub} r={r} nested />
          ))}
        </li>
      ))}
    </Tag>
  );
}

function BlockView({ block, r }: { block: Block; r: Render }) {
  switch (block.type) {
    case "paragraph":
      return <div className="whitespace-pre-wrap">{inlines(block.children, r)}</div>;
    case "quote":
      return (
        <blockquote className="m-0 border-l-[3px] border-line-strong pl-3 whitespace-pre-wrap text-ink-secondary">
          {inlines(block.children, r)}
        </blockquote>
      );
    case "code_block":
      return <CodeBlock code={block.text} lang={block.lang} copy={r.copy} />;
    case "list":
      return <ListView list={block} r={r} />;
  }
}

/**
 * A message body. A plain body, or a formatted one that is a single paragraph, renders as inline
 * text so the time and ticks can sit at the end of its last line as before.
 */
export const RichText = /* @__PURE__ */ memo(function RichText({
  body,
  format,
  mentions,
  openExternal,
  renderMention,
  renderChannel,
  copy,
  renderText,
  className,
}: RichTextProps) {
  const doc = useMemo(() => parse(body, { format, mentions }), [body, format, mentions]);
  const r: Render = { openExternal, renderMention, renderChannel, copy, renderText };
  const [only] = doc.blocks;
  if (doc.blocks.length === 0) return null;
  if (doc.blocks.length === 1 && only?.type === "paragraph") {
    return <span className={cn("whitespace-pre-wrap", className)}>{inlines(only.children, r)}</span>;
  }
  return (
    <div className={cn("flex flex-col gap-1.5 [overflow-wrap:anywhere]", className)} data-rich-text="">
      {doc.blocks.map((b, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: blocks keep their order for a given body
        <BlockView key={i} block={b} r={r} />
      ))}
    </div>
  );
});
